import * as THREE from 'three';
import type { SiteObject } from './SiteObject';

/**
 * The seam between procedurally generated site objects and authored 3D models — the same bet
 * {@link FloraModelSource} makes for plants, applied to the built environment.
 *
 * `SiteObjectWorld` asks this registry for a provider before falling back to
 * {@link buildSiteObjectGeometry}. Registering a provider for a model id swaps that object over to an
 * authored asset without touching the editor, the serializer, the keep-out test, or the capture
 * manifest — because none of those ever look at geometry. They look at the record.
 *
 * ## Why the record still owns the object
 *
 * A prop is a **record that names a mesh**, never a record that contains one. The document stores
 * `modelId: 'bench-slatted'`; the mesh lives in the asset library beside the catalog that describes
 * it. That keeps every property the procedural design was chosen for: the project file stays small
 * and diff-able, the object stays editable, it lights and shadows under the live sun like everything
 * else, and a document opened without its assets still renders — as placeholder massing rather than
 * as nothing. See `docs/SITE_FROM_REFERENCE.md`.
 *
 * The consequence worth stating plainly: a missing asset is a *degraded* object, never a broken
 * document. That is why {@link SiteObjectModelProvider.build} may return `null` at will.
 *
 * ## Roles
 *
 * Providers are expected to tag their meshes with a {@link SiteMeshRole} so the rest of the pipeline
 * can reason about them without knowing what the asset is:
 *
 * - `body`     — the bulk of the object; takes the record's `wallColor` when styled.
 * - `trim`     — secondary surfaces (a bench's ironwork, a lamp's post); takes `roofColor`.
 * - `glass`    — transparent panes, so a lantern is not a solid brick of colour.
 * - `emissive` — the lit part of a fixture. Held apart so path lighting can glow after sundown.
 * - `foliage`  — living material on an otherwise built object, e.g. a planted urn.
 *
 * `foliage` is deliberately the same word the flora system uses. Nothing drives it seasonally yet —
 * that is a wiring job against `FloraSeasonModel`, not a new concept — but naming it now means the
 * assets imported today will not have to be re-tagged when it lands.
 */

export const SITE_MESH_ROLES = ['body', 'trim', 'glass', 'emissive', 'foliage'] as const;
export type SiteMeshRole = (typeof SITE_MESH_ROLES)[number];

/**
 * Classify a scene node by naming convention, mirroring `meshRole` in the flora pipeline.
 *
 * Most downloaded assets match nothing here — a scanned boulder's mesh is called `boulder_01` — and
 * fall to `body`, which is the right default: one material, the object's primary colour. Roles earn
 * their keep on authored or hand-tagged assets, and on catalog entries that override them.
 */
export function siteMeshRole(name: string): SiteMeshRole {
  const lower = name.toLowerCase();
  if (lower.includes('glass') || lower.includes('pane') || lower.includes('window')) return 'glass';
  if (lower.includes('emissive') || lower.includes('light') || lower.includes('lamp')) return 'emissive';
  if (lower.includes('foliage') || lower.includes('leaf') || lower.includes('leaves')) return 'foliage';
  if (lower.includes('trim') || lower.includes('metal') || lower.includes('frame')) return 'trim';
  return 'body';
}

/**
 * How an asset is fitted to the dimensions on the record.
 *
 * The distinction matters because manufactured objects and terrain features fail differently. A
 * bench stretched to fill an arbitrary footprint reads as broken; a boulder stretched reads as a
 * different boulder. So proportion is preserved by default, and anything else is opt-in per catalog
 * entry.
 *
 * - `uniform` — one scale factor, the most constraining axis wins. The object always fits inside the
 *   record's box and never distorts. Correct for furniture, fixtures, and structures.
 * - `stretch` — per-axis, filling the box exactly. For things that genuinely are made to length:
 *   fence runs, decking, edging.
 * - `natural` — the asset's authored scale, ignoring the record's dimensions entirely. For assets
 *   whose real-world size *is* the point, and which the catalog has measured honestly.
 */
export type SiteModelFit = 'uniform' | 'stretch' | 'natural';

export const SITE_MODEL_FITS: readonly SiteModelFit[] = ['uniform', 'stretch', 'natural'];

/**
 * How much of the asset's own look survives.
 *
 * This is the control that decides whether an imported mesh joins the scene or sits on top of it.
 * Procedural buildings here are flat-shaded, untextured, and lit purely by the sun model; a
 * photoscanned prop arrives with 2K PBR maps and its own baked lighting. Dropping the second into
 * the first is the single most reliable way to make a scene look assembled from parts.
 *
 * - `house`  — discard the asset's materials entirely and rebuild them from the record's colours,
 *   flat-shaded, to match `SiteObjectWorld`'s procedural output. Maximum coherence.
 * - `tinted` — keep the base colour map, discard normal/AO/roughness/metalness maps, and multiply by
 *   the record's colour. Keeps the information a texture carries (the mortar lines on a fire pit)
 *   without the photoreal shading that clashes.
 * - `asset`  — leave the author's materials alone. For the rare asset whose texture is the whole
 *   reason it was chosen.
 */
export type SiteModelStyle = 'house' | 'tinted' | 'asset';

export const SITE_MODEL_STYLES: readonly SiteModelStyle[] = ['house', 'tinted', 'asset'];

export interface SiteObjectModelRequest {
  /** The record being built. Providers read dimensions, colours, and rotation from here. */
  object: SiteObject;
  /**
   * How far the ground falls away beneath the footprint, in meters — the same figure the procedural
   * path receives as a skirt. Most props ignore it; a provider for something that meets the ground
   * along a run, like decking, may want to close the gap.
   */
  groundSkirtMeters: number;
}

export interface SiteObjectModelProvider {
  /** Stable identifier, surfaced in the inspector so a designer can see which asset is in use. */
  readonly id: string;
  /**
   * Build a view for this request, or return `null` to defer to the procedural generator.
   *
   * Returning `null` is a normal outcome, not an error path: it is what a provider does while its
   * asset is still downloading, and what it does for a request it has no asset for.
   */
  build(request: SiteObjectModelRequest): THREE.Object3D | null;
}

/**
 * Maps `SiteObject.modelId` values to authored-model providers. Empty by default — every site object
 * is procedural until something registers here.
 *
 * Keyed by model id rather than by object id, because the relationship is many-to-one: a courtyard
 * with twelve of the same chair is twelve records sharing one asset.
 */
export class SiteObjectModelRegistry {
  private readonly byModel = new Map<string, SiteObjectModelProvider>();
  private readonly listeners = new Set<(modelId: string) => void>();

  register(modelId: string, provider: SiteObjectModelProvider): void {
    this.byModel.set(modelId, provider);
    for (const listener of this.listeners) listener(modelId);
  }

  unregister(modelId: string): boolean {
    const removed = this.byModel.delete(modelId);
    if (removed) for (const listener of this.listeners) listener(modelId);
    return removed;
  }

  has(modelId: string): boolean {
    return this.byModel.has(modelId);
  }

  resolve(modelId: string): SiteObjectModelProvider | undefined {
    return this.byModel.get(modelId);
  }

  /** Model ids currently backed by an authored asset. */
  registeredIds(): string[] {
    return [...this.byModel.keys()];
  }

  /**
   * Subscribe to registration changes. Assets load asynchronously, so a view built before its model
   * arrived has to be told to rebuild — otherwise a project opens as placeholder boxes and stays
   * that way until something else happens to touch it. Returns an unsubscribe function.
   */
  onChange(listener: (modelId: string) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  clear(): void {
    this.byModel.clear();
  }
}

/**
 * The registry the application uses. `PropLibrary` populates it as assets finish loading; anything
 * else that can produce a site-object mesh may register here too.
 */
export const siteModels = new SiteObjectModelRegistry();

/* ------------------------------- Fitting and styling ------------------------------- */

/**
 * Measure a prototype and return the transform that seats it: centred on X/Z, resting on y = 0.
 *
 * Assets do not agree on where their origin is. Poly Haven's are usually base-centred, Kenney's are
 * sometimes centred on the bounding box, and a generated mesh is wherever the generator left it. So
 * rather than trust the origin, we measure — once, on the prototype — and every instance inherits a
 * known seating. Without this a bench floats or sinks by half its height depending on who made it.
 */
export function seatingOffset(prototype: THREE.Object3D): { offset: THREE.Vector3; size: THREE.Vector3 } {
  prototype.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(prototype);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();

  if (box.isEmpty()) return { offset: new THREE.Vector3(), size: new THREE.Vector3(1, 1, 1) };

  box.getSize(size);
  box.getCenter(center);

  // X/Z to the footprint centre, Y so the lowest point lands on the ground plane.
  return { offset: new THREE.Vector3(-center.x, -box.min.y, -center.z), size };
}

/**
 * Scale factors that fit an asset of `size` into the record's box under the given fit mode.
 *
 * A zero or missing dimension on the record means "unconstrained on this axis" rather than "collapse
 * to nothing" — a prop that has never had its height set should not vanish.
 */
export function fitScale(
  size: THREE.Vector3,
  target: { width: number; depth: number; height: number },
  fit: SiteModelFit,
): THREE.Vector3 {
  if (fit === 'natural') return new THREE.Vector3(1, 1, 1);

  const ratio = (want: number, have: number): number =>
    want > 0 && have > 1e-6 ? want / have : Number.POSITIVE_INFINITY;

  const sx = ratio(target.width, size.x);
  const sy = ratio(target.height, size.y);
  const sz = ratio(target.depth, size.z);

  if (fit === 'stretch') {
    const finite = (value: number): number => (Number.isFinite(value) ? value : 1);
    return new THREE.Vector3(finite(sx), finite(sy), finite(sz));
  }

  // Uniform: the most constraining axis wins, so the asset always fits inside the record's box.
  const smallest = Math.min(sx, sy, sz);
  const scale = Number.isFinite(smallest) && smallest > 0 ? smallest : 1;
  return new THREE.Vector3(scale, scale, scale);
}

/**
 * Rebuild an instance's materials according to `style`, tagging each node with its role on the way.
 *
 * Materials are always cloned or replaced per instance, never shared with the prototype, for the same
 * reason the flora path clones them: a per-object colour edit must not leak across every other object
 * using the same asset.
 */
export function applySiteObjectStyle(
  root: THREE.Object3D,
  object: SiteObject,
  style: SiteModelStyle,
): void {
  root.traverse((node) => {
    const mesh = node as Partial<THREE.Mesh> & THREE.Object3D;
    if (mesh.isMesh !== true || !mesh.material) return;

    const source = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const role = siteMeshRole(node.name || source?.name || '');
    node.name = role;
    node.castShadow = role !== 'glass';
    node.receiveShadow = true;

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const rebuilt = materials.map((material) => styleMaterial(material, role, object, style));
    mesh.material = Array.isArray(mesh.material) ? rebuilt : (rebuilt[0] as THREE.Material);
  });
}

function styleMaterial(
  source: THREE.Material,
  role: SiteMeshRole,
  object: SiteObject,
  style: SiteModelStyle,
): THREE.Material {
  if (style === 'asset') return source.clone();

  const tint = new THREE.Color(role === 'trim' ? object.roofColor : object.wallColor);

  if (role === 'glass') {
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color('#cfe0e6'),
      roughness: 0.1,
      metalness: 0,
      transparent: true,
      opacity: 0.35,
    });
  }

  if (role === 'emissive') {
    return new THREE.MeshStandardMaterial({
      color: tint,
      emissive: new THREE.Color('#ffd9a0'),
      // Left dark here; the environment is what should decide a lamp is lit, not the asset importer.
      emissiveIntensity: 0,
      roughness: 0.5,
      metalness: 0,
    });
  }

  if (style === 'tinted') {
    // Keep the albedo, drop everything that carries the asset's own lighting. Normal and AO maps are
    // what make a photoscan read as photoreal next to flat-shaded geometry, so they are exactly what
    // has to go.
    const standard = source as Partial<THREE.MeshStandardMaterial>;
    const map = standard.map ?? null;

    // White where a map survives, and *only* there.
    //
    // Three multiplies `color` by `map`, so tinting a textured mesh with the record's colour
    // multiplies two mid-tones and lands near black — which is exactly what a rock rendered at
    // #8a8378 over its own albedo did. The record's colour still governs any mesh in the asset that
    // has no texture of its own, which is the case the field was really for.
    return new THREE.MeshStandardMaterial({
      color: map ? new THREE.Color('#ffffff') : tint,
      map,
      roughness: 0.8,
      metalness: 0,
    });
  }

  return new THREE.MeshStandardMaterial({
    color: tint,
    roughness: role === 'trim' ? 0.6 : 0.78,
    metalness: 0,
    flatShading: true,
  });
}
